import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent } from './ui/dialog';
import { Button } from './ui/button';
import { Check, X, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Analytics tracking helper
const trackEvent = (eventName, data = {}) => {
  // Log for now - integrate with analytics provider later
  console.log(`[Analytics] ${eventName}:`, { ...data, timestamp: new Date().toISOString() });
  
  // Store in sessionStorage for debugging/development
  const events = JSON.parse(sessionStorage.getItem('premiumModalEvents') || '[]');
  events.push({ event: eventName, data, timestamp: new Date().toISOString() });
  sessionStorage.setItem('premiumModalEvents', JSON.stringify(events));
};

const PremiumUpgradeModal = ({ open, onOpenChange, onSubscribe }) => {
  const { t } = useTranslation();
  const [selectedPlan, setSelectedPlan] = useState('annual');
  const [modalOpenTime, setModalOpenTime] = useState(null);
  const [loading, setLoading] = useState(false);

  // Track modal view on open
  useEffect(() => {
    if (open) {
      setModalOpenTime(Date.now());
      trackEvent('modal_view', { source: 'premium_upgrade' });
    }
  }, [open]);

  // Track modal close
  const handleClose = () => {
    if (modalOpenTime) {
      const timeSpent = Math.round((Date.now() - modalOpenTime) / 1000);
      trackEvent('modal_close', { time_spent_seconds: timeSpent, selected_plan: selectedPlan });
    }
    onOpenChange(false);
  };

  // Track plan selection
  const handlePlanSelect = (plan) => {
    setSelectedPlan(plan);
    trackEvent('plan_selected', { plan });
  };

  // Handle upgrade action - NOW WITH REAL STRIPE CHECKOUT
  const handleUpgrade = async () => {
    const timeToConversion = modalOpenTime ? Math.round((Date.now() - modalOpenTime) / 1000) : 0;
    trackEvent('upgrade_conversion', { 
      plan: selectedPlan, 
      time_to_conversion_seconds: timeToConversion,
      price: selectedPlan === 'annual' ? 99 : 9.99
    });
    
    setLoading(true);
    
    try {
      // Get auth token
      const token = sessionStorage.getItem('prosperMindToken') || sessionStorage.getItem('authToken');
      if (!token) {
        toast.error(t('premium.loginRequired', 'Please log in to upgrade'));
        setLoading(false);
        return;
      }
      
      // Create Stripe checkout session
      const response = await fetch(`${API_URL}/api/stripe/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          origin_url: window.location.origin
          // Note: For now using the default price. 
          // To support monthly vs annual, you'd need separate price IDs in Stripe
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create checkout session');
      }
      
      const data = await response.json();
      
      // Redirect to Stripe Checkout
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL received');
      }
      
    } catch (error) {
      console.error('Stripe checkout error:', error);
      toast.error(error.message || t('premium.checkoutError', 'Failed to start checkout. Please try again.'));
      setLoading(false);
    }
  };

  // Optimized 6-item feature list (removed "Financial journal & reflection tools")
  const features = [
    t('premium.feature1', 'Personalized Wealth Ladder roadmap'),
    t('premium.feature2', 'Advanced budgeting & spending insights'),
    t('premium.feature3', 'Cash flow forecasting & projections'),
    t('premium.feature4', 'Value creation & income growth tools'),
    t('premium.feature5', 'Identity, beliefs & habit optimization modules'),
    t('premium.feature6', 'Full ProsperMind Academy access'),
  ];

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg p-0 bg-[#0D0D0D] border border-gray-800 rounded-2xl overflow-hidden">
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute right-4 top-4 p-1 rounded-full hover:bg-gray-800 transition-colors z-10"
          aria-label="Close"
        >
          <X className="w-5 h-5 text-gray-500 hover:text-gray-300" />
        </button>

        <div className="px-8 py-10">
          {/* Title */}
          <h2 className="text-2xl md:text-3xl font-semibold text-white text-center mb-2">
            {t('premium.title', 'Unlock Your Financial Roadmap')}
          </h2>

          {/* Value Statement - Updated copy */}
          <p className="text-gray-400 text-center text-sm md:text-base mb-3 max-w-md mx-auto leading-relaxed">
            {t('premium.valueStatement', 'Build, grow, and control your financial future with ProsperMind\'s full system.')}
          </p>

          {/* Trust Line - Social proof */}
          <p className="text-gray-500 text-center text-xs mb-8">
            {t('premium.trustLine', 'Join thousands of users improving their financial future.')}
          </p>

          {/* Feature Benefits List - 6 items */}
          <div className="space-y-3.5 mb-8">
            {features.map((feature, index) => (
              <div key={index} className="flex items-start gap-3">
                <div className="flex-shrink-0 mt-0.5">
                  <Check className="w-5 h-5 text-emerald-500" strokeWidth={2.5} />
                </div>
                <span className="text-gray-300 text-sm md:text-base leading-snug">
                  {feature}
                </span>
              </div>
            ))}
          </div>

          {/* Divider */}
          <div className="h-px bg-gradient-to-r from-transparent via-gray-700 to-transparent mb-8" />

          {/* Pricing Options */}
          <div className="space-y-3 mb-8">
            {/* Annual Plan - Recommended - Emphasized pricing hierarchy */}
            <button
              onClick={() => handlePlanSelect('annual')}
              className={`w-full p-5 pr-12 rounded-xl border-2 transition-all duration-200 text-left relative ${
                selectedPlan === 'annual'
                  ? 'border-amber-500/70 bg-amber-500/5'
                  : 'border-gray-800 hover:border-gray-700 bg-gray-900/30'
              }`}
            >
              {/* Most Popular Badge */}
              <div className="absolute -top-2.5 left-4">
                <span className="px-2.5 py-0.5 bg-amber-500 text-black text-xs font-semibold rounded-full">
                  {t('premium.mostPopular', 'Most Popular')}
                </span>
              </div>

              <div className="flex items-center justify-between mt-1">
                <div>
                  {/* Plan name - secondary emphasis */}
                  <div className="text-gray-400 text-xs uppercase tracking-wide mb-1">
                    {t('premium.annualPlan', 'Annual Plan')}
                  </div>
                  {/* Savings message - tertiary emphasis */}
                  <div className="text-emerald-400 text-xs font-medium">
                    {t('premium.saveDollar', 'Save $20 (2 months free)')}
                  </div>
                </div>
                <div className="text-right">
                  {/* Price - primary emphasis */}
                  <div className="text-white text-2xl font-bold">
                    $99<span className="text-gray-500 text-sm font-normal"> / {t('premium.year', 'year')}</span>
                  </div>
                </div>
              </div>

              {/* Selection indicator */}
              <div className={`absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                selectedPlan === 'annual' 
                  ? 'border-amber-500 bg-amber-500' 
                  : 'border-gray-600'
              }`}>
                {selectedPlan === 'annual' && (
                  <Check className="w-3 h-3 text-black" strokeWidth={3} />
                )}
              </div>
            </button>

            {/* Monthly Plan */}
            <button
              onClick={() => handlePlanSelect('monthly')}
              className={`w-full p-4 pr-12 rounded-xl border-2 transition-all duration-200 text-left relative ${
                selectedPlan === 'monthly'
                  ? 'border-amber-500/70 bg-amber-500/5'
                  : 'border-gray-800 hover:border-gray-700 bg-gray-900/30'
              }`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-gray-400 text-xs uppercase tracking-wide mb-1">
                    {t('premium.monthlyPlan', 'Monthly Plan')}
                  </div>
                  <div className="text-gray-500 text-xs">
                    {t('premium.perDay', '≈ $0.33 per day')}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-white text-xl font-semibold">
                    $9.99<span className="text-gray-500 text-sm font-normal"> / {t('premium.month', 'month')}</span>
                  </div>
                </div>
              </div>

              {/* Selection indicator */}
              <div className={`absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                selectedPlan === 'monthly' 
                  ? 'border-amber-500 bg-amber-500' 
                  : 'border-gray-600'
              }`}>
                {selectedPlan === 'monthly' && (
                  <Check className="w-3 h-3 text-black" strokeWidth={3} />
                )}
              </div>
            </button>
          </div>

          {/* CTA Button */}
          <Button
            onClick={handleUpgrade}
            disabled={loading}
            className="w-full py-4 text-base font-semibold text-black rounded-xl transition-all duration-300 hover:scale-[1.02] hover:shadow-lg disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:scale-100"
            style={{
              background: 'linear-gradient(135deg, #D4A84B 0%, #F5D98A 50%, #D4A84B 100%)',
            }}
            data-testid="premium-unlock-button"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                {t('premium.processing', 'Processing...')}
              </>
            ) : (
              t('premium.unlockPremium', 'Unlock Premium')
            )}
          </Button>

          {/* Risk-Free Language */}
          <p className="text-center text-gray-400 text-xs mt-4 font-medium">
            {t('premium.tryRiskFree', 'Try risk-free')}
          </p>
          
          {/* Cancel Anytime Text */}
          <p className="text-center text-gray-500 text-xs mt-1">
            {t('premium.cancelAnytime', 'Cancel anytime. No commitment.')}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PremiumUpgradeModal;
